import { createContext, useMemo, useEffect, useRef, useContext } from "react";
import useTrackedState from "../CustomHooks/useTrackedState";
import { isJuvenile, isYoeAge } from "../utils/utils";
import { UserContext } from "./UserContext";
import { IncidentsContext } from "./IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
// Create the context
export const SingleIncidentContext = createContext();

// Define the provider component
export const SingleIncidentProvider = ({
  children,
  incident,
  user,
  updatePis,
}) => {
  // Ref to store previous yoeJuvenileStatuses
  const prevYoeJuvenileStatusesRef = useRef([]);
  const { onlineState } = useContext(UserContext);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { UPDATE_PIS } = INCIDENT_ACTIONS;
  // Extract callTime using useMemo
  const callTime = useMemo(() => incident?.callTime, [incident?.callTime]);

  // Track callTime
  const trackedCallTime = useTrackedState(callTime);

  // Extract people data using useMemo
  const people = useMemo(() => {
    return (
      incident?.personIncidentSpecifics?.map(pis => {
        const dob = pis?.person?.personalAttribute?.dob;
        const juvenileAge = isJuvenile(dob, trackedCallTime);
        const yoeAge = isYoeAge(dob, trackedCallTime);
        return {
          pisId: pis?.id,
          id: pis?.person?.id,
          dob,
          juvenileAge,
          yoeAge,
          pisYoe: pis?.yoe,
          ...pis?.person,
        };
      }) || []
    );
  }, [incident?.personIncidentSpecifics, trackedCallTime]);

  // Track people data
  const trackedPeople = useTrackedState(people);

  // Extract offenses data using useMemo
  const offenses = useMemo(() => {
    return (
      incident?.incidentDetail?.incidentOffenses?.map(offense => {
        const offenseYoe = offense?.offenseViolationPrototype?.yoe;
        const suspects = offense?.suspectVictimAffiliations?.map(
          affiliation => affiliation?.suspect?.id
        );
        const victims = offense?.suspectVictimAffiliations?.map(
          affiliation => affiliation?.victim?.id
        );
        return {
          offenseId: offense?.id,
          offenseYoe,
          suspects,
          victims,
        };
      }) || []
    );
  }, [incident?.incidentDetail?.incidentOffenses]);

  // Track offenses data
  const trackedOffenses = useTrackedState(offenses);

  const arrests = useMemo(() => {
    return incident?.incidentArrests?.map(arrest => {
      const aChargeIsYoe = arrest?.arrestCharges?.some(
        charge => charge?.offenseViolationPrototype?.yoe
      );
      const arrestee = arrest?.arrestee;
      return {
        arrestId: arrest?.id,
        aChargeIsYoe,
        arrestee,
      };
    });
  }, [incident?.incidentArrests]);

  const trackedArrests = useTrackedState(arrests);

  // Determine if a person is a suspect or victim
  const yoeJuvenileStatuses = useMemo(() => {
    return (
      trackedPeople?.map(person => {
        const isSuspect = trackedOffenses?.some(offense =>
          offense?.suspects?.includes(person?.id)
        );
        const isVictim = trackedOffenses?.some(offense =>
          offense?.victims?.includes(person?.id)
        );
        const isArrestee = trackedArrests?.some(
          arrest => arrest?.arrestee?.id === person?.id
        );
        const isJuvenile =
          person?.juvenileAge && (isSuspect || isVictim || isArrestee);
        const isYoeEligible =
          !isJuvenile &&
          person?.yoeAge &&
          (trackedOffenses?.some(
            offense =>
              offense?.offenseYoe &&
              (offense?.suspects?.includes(person?.id) ||
                offense?.victims?.includes(person?.id))
          ) ||
            trackedArrests?.some(
              arrest =>
                arrest?.aChargeIsYoe && arrest?.arrestee?.id === person?.id
            ));
        return {
          pisId: person?.pisId,
          person,
          isJuvenile,
          isYoeEligible,
          yoe: person?.pisYoe,
        };
      }) || []
    );
  }, [trackedPeople, trackedOffenses, trackedArrests]);

  // Ensure juvenile status is updated if necessary
  useEffect(() => {
    if (onlineState) {
      yoeJuvenileStatuses?.forEach(status => {
        const personSpecific = incident?.personIncidentSpecifics?.find(
          pis => pis?.person?.id?.toString() === status?.person?.id?.toString()
        );

        if (personSpecific) {
          const originatorId = user?.id;
          const personIncidentSpecificId =
            personSpecific?.id || personSpecific?.uuid;
          const incidentId = incident?.id;

          // Find the previous status for comparison
          const prevStatus = prevYoeJuvenileStatusesRef?.current?.find(
            prev => prev?.pisId === status?.pisId
          );

          // Update juvenile status if necessary
          if (
            (prevStatus && prevStatus?.isJuvenile !== status?.isJuvenile) ||
            (Object.keys(personSpecific).includes("juvenile") &&
              personSpecific?.juvenile !== status?.isJuvenile)
          ) {
            if (originatorId && incidentId && personIncidentSpecificId) {
              console.log("Updating juvenile status via PIS mutation");
              updatePis({
                variables: {
                  input: {
                    originatorId,
                    incidentId,
                    personIncidentSpecificId,
                    juvenile: status?.isJuvenile,
                  },
                },
              });
              if (!onlineState) {
                console.log("Offline juvenile update");
                incidentDispatch({
                  type: UPDATE_PIS,
                  payload: {
                    incidentId: incident?.id || incident?.uuid,
                    pisId: personIncidentSpecificId,
                    juvenile: status?.isJuvenile,
                  },
                });
              }
            }
          }

          // Clear yoe status if necessary
          if (personSpecific?.yoe && !status?.isYoeEligible) {
            if (originatorId && incidentId && personIncidentSpecificId) {
              updatePis({
                variables: {
                  input: {
                    originatorId,
                    incidentId,
                    personIncidentSpecificId,
                    yoe: false,
                  },
                },
              });
            }
          }
        }
      });
    }

    // Update the previous statuses ref
    prevYoeJuvenileStatusesRef.current = yoeJuvenileStatuses;
  }, [
    yoeJuvenileStatuses,
    incident,
    updatePis,
    user?.id,
    onlineState,
    incidentDispatch,
    UPDATE_PIS,
  ]);

  return (
    <SingleIncidentContext.Provider
      value={{
        incident,
        yoeJuvenileStatuses,
      }}
    >
      {children}
    </SingleIncidentContext.Provider>
  );
};
